<template>
  <div class="datepicker-container">
    <!--Input Text-->
    <input
      type="text"
      class="datepicker-input"
      @click="openModal"
      :placeholder="inputProps.placeholder || placeholder"
      readonly
      :value="formattedDateString"
    >
    <div class="datepicker-icon">
      <slot>
        <span></span>
      </slot>
    </div>
    
    <!-- Custom Modal Overlay -->
    <div v-if="datePickerModal" class="modal-overlay" @click="closeModal">
      <div @click.stop class="modal-content">
        <CustomDatePickerContent
          @updateDate="handleDateUpdate"
          :disabledDates="disabledDates"
          :previous-selected-date="date"
          :min-date="minDate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CustomDatePickerContent from '@/components/CustomDatePickerContent.vue';
export default {
  name: "CustomDatepickerV2",
  props: {
    value: {
        type: Date,
        default: null,
    },
    placeholder: {
      type: String,
      default: 'Select a date...'
    },
    locale: {
      type: String,
      default: 'en-GB'
    },
    inputProps: Object,
    minDate: Date,
    pickupMonth: {
      type: Number,
      default: null
    },
    pickupYear: {
      type: Number,
      default: null
    }
  },
  components: {
    CustomDatePickerContent
  },
  data() {
    return {
      date: this.value,
      datePickerModal: false,
    }
  },
  watch: {
    value(newVal) {
        this.date = newVal;
    },
  },
  methods: {
    openModal() {
      this.datePickerModal = true;
    },
    closeModal() {
      this.datePickerModal = false;
    },
    handleDateUpdate(newDate) {
      this.date = newDate;
      this.$emit('input', newDate);
      this.$emit('dateSelected', newDate.getMonth(), newDate.getFullYear());  // Emit the selected month and year
      this.closeModal();
    },
    isValidDate(date) {
      return date && date >= this.minDate;
    },
    padWithZero(num) {
      return num < 10 ? `0${num}` : num;
    }
  },
  computed: {
    formattedDate() {
      if (!this.date) return "";

      let days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      let months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

      let dateObj = new Date(this.date);
      
      let dayOfWeek = days[dateObj.getDay()];
      let dayOfMonth = dateObj.getDate();
      let month = months[dateObj.getMonth()];
      let year = dateObj.getFullYear();

      return {
        dayOfWeek,
        month,
        dayOfMonth,
        year
      }
    },
    formattedDateString() {
      if (!this.date) return "";
      return `${this.padWithZero(this.date.getDate())}/${this.padWithZero(this.date.getMonth() + 1)}/${this.date.getFullYear()}`;
    },
    disabledDates() {
        return [{
            start: null,
            end: new Date(new Date().setDate(new Date().getDate() - 1))
        }];
    }

  },
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/settings.scss";
.datepicker-container {
  position: relative;  // This is essential
  width: 100%;
}
.datepicker-input {
  appearance: none; // attempt to remove browser default styles
  -webkit-appearance: none;
  -moz-appearance: none;

  background-color: #f6f6f4 !important;
  height: 64px !important;
  border-radius: 4px !important;
  border: none !important;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  font-size: 16px !important;
  width: 100% !important;
  display: flex;
  align-items: center;
  padding: 16px;
  justify-content: space-between;
  cursor: pointer;
  
  &:hover, &:focus {
    outline: none;
    box-shadow: none;
  }

}

.datepicker-input ::v-deep input[type=text] {
  background: none;
  border: none;
  padding: 0 !important;
  padding-right: 40px;
}

.datepicker-icon {
  position: absolute;
  right: 16px; 
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.datepicker-container:nth-of-type(1) {
  margin-right: 0.75em;
}

.datepicker-container:nth-of-type(1) .datepicker-icon {
  right: 32px;
  position: absolute;
}

/* Custom Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3); /* semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* To ensure the modal is above other page content */
}

.modal-content {
  background: white;
  padding: 20px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  max-width: 343px;  /* Set max-width */
  width: 100%;       /* This ensures the content tries to occupy the full width up to max-width */
  display: flex;     /* Flex will help ensure that child elements can expand to full width */
  flex-direction: column; /* This ensures that the content is arranged vertically */
}

.datepicker-header {
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.01em;
  text-align: left;
  margin-bottom: 16px;
  border-bottom: 0px;
}

.datepicker-chip-wrapper {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.datepicker-chip {
  display: inline-block;
  padding: 8px 16px; // 8px vertical padding, 16px horizontal
  border: 2px solid #D1D2D6;
  border-radius: 24px; // Rounded edges
  background-color: #ffffff; // Change to desired background color
  color: #30384A; // Text color
  font-family: Inter;
  font-size: 14px;
  font-weight: 500; // Slightly bold
  transition: background-color 0.2s ease; // Smooth color transition
  
  &:hover {
    background-color: #40495a; // Slightly lighter color on hover
  }
}

.datepicker-footer {
  
  .datepicker-chip-wrapper {
    justify-content: flex-end; // This will right-align items within a flex container
  }
}

.datepicker-chip.button {
  display: inline-flex;  // Use flex to center content
  align-items: center;  // Vertically center
  justify-content: center;  // Horizontally center
  line-height: 1;  // Reset line height
  vertical-align: middle;  // For inline-block
  height: auto;  // Reset any default height
  
  // Other styles remain unchanged
  cursor: pointer; // To indicate it's clickable

  &:hover {
    background-color: #40495a; // Darker shade on hover for button
    color: #ffffff;
  }
}
// Mobile styles
@media(max-width: 1120px) {
  .datepicker-container:nth-of-type(1) {
    padding-right: 0;
  }
  .datepicker-container:nth-of-type(1) .datepicker-icon {
    right: 16px;
  }
}

</style>
